import { useState } from "react";
import { PhotoTile } from "./PhotoTile.js";
import { stripExifIfImage } from "../lib/strip-exif.js";
import type { DocumentKind } from "../api/types.js";

export const MAX_BYTES = 10 * 1024 * 1024; // 10 MB — matches the upload-url limit
const ACCEPT_TYPES = "image/jpeg,image/png,image/webp,application/pdf";

export const DOC_LABEL: Record<DocumentKind, string> = {
  drivers_license_front: "Driver's license — front",
  drivers_license_back:  "Driver's license — back",
  insurance_front:       "Insurance card — front",
  insurance_back:        "Insurance card — back",
};

// Upload tiles are grouped by document, front + back side by side.
const DOC_GROUPS: ReadonlyArray<{
  title: string;
  docs: ReadonlyArray<{ kind: DocumentKind; caption: string }>;
}> = [
  { title: "Driver's license", docs: [
    { kind: "drivers_license_front", caption: "Front" },
    { kind: "drivers_license_back",  caption: "Back"  },
  ] },
  { title: "Insurance card", docs: [
    { kind: "insurance_front", caption: "Front" },
    { kind: "insurance_back",  caption: "Back"  },
  ] },
];

type Props = {
  // Which kinds to render slots for; groups with no kinds are omitted.
  kinds: readonly DocumentKind[];
  // Kinds already on file; these render as uploaded.
  uploaded: readonly DocumentKind[];
  // Performs the whole upload for one file (upload-url, PUT, commit).
  // Throw to show the message inline on the tile.
  upload: (kind: DocumentKind, file: File) => Promise<void>;
  onUploaded: (kind: DocumentKind) => void;
  // Called with every upload failure so the parent can react (e.g. 404).
  onError?: (err: unknown) => void;
};

export function DocumentUploadSlots({ kinds, uploaded, upload, onUploaded, onError }: Props) {
  const [uploading, setUploading] = useState<Partial<Record<DocumentKind, boolean>>>({});
  const [errors, setErrors] = useState<Partial<Record<DocumentKind, string>>>({});

  function patch<T>(set: (fn: (p: Partial<Record<DocumentKind, T>>) => Partial<Record<DocumentKind, T>>) => void,
    kind: DocumentKind, value: T | undefined) {
    set((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[kind]; else next[kind] = value;
      return next;
    });
  }

  async function handleSelect(kind: DocumentKind, file: File) {
    if (file.size > MAX_BYTES) {
      patch(setErrors, kind, `File too large (max ${MAX_BYTES / (1024 * 1024)} MB)`);
      return;
    }
    patch(setErrors, kind, undefined);
    patch(setUploading, kind, true);
    try {
      const stripped = await stripExifIfImage(file);
      await upload(kind, stripped);
      onUploaded(kind);
    } catch (err) {
      patch(setErrors, kind, err instanceof Error ? err.message : "Upload failed");
      onError?.(err);
    } finally {
      patch(setUploading, kind, undefined);
    }
  }

  return (
    <div className="grid gap-3">
      {DOC_GROUPS.map((group) => {
        const docs = group.docs.filter((d) => kinds.includes(d.kind));
        if (docs.length === 0) return null;
        return (
          <div
            key={group.title}
            className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
          >
            <p className="text-sm font-medium text-slate-700">{group.title}</p>
            <div className="mt-3 grid grid-cols-2 gap-4">
              {docs.map(({ kind, caption }) => (
                <PhotoTile
                  key={kind}
                  label={DOC_LABEL[kind]}
                  caption={caption}
                  accept={ACCEPT_TYPES}
                  uploaded={uploaded.includes(kind)}
                  uploading={!!uploading[kind]}
                  error={errors[kind]}
                  onSelect={(f) => void handleSelect(kind, f)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
